import React, { useState, useRef, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import api from "../../Services/api";
import Swal from "sweetalert2";
import ResultadoUpload from "./resultadoUpload";
import { RiDragDropLine } from "react-icons/ri";
import { IoCloseCircle } from "react-icons/io5";
import { LiaCloudUploadAltSolid } from "react-icons/lia";
import { useOperador } from "../../Hooks/useOperador";
import "./ProgressoGeral.css";

export default function UploaderArquivos({
  tipoPermissao,
  bucketName,
  IDDiretorio,
  setExibirUploader,
  setAtualizarArquivos,
  permitirDocs = true,
}) {
  const [arquivos, setArquivos] = useState([]);
  const [progresso, setProgresso] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [uploadFinalizado, setUploadFinalizado] = useState(false);
  const { IDOperador } = useOperador();
  const fileInputRef = useRef(null);
  const evtSourceRef = useRef(null);
  const uploadConcluidoRef = useRef(false);

  // Quantidade máxima de arquivos permitida por requisição
  const MAX_FRONTEND_FILES = 50;
  const MAX_FILE_SIZE = 800 * 1024 * 1024; // 800MB

  const getProgressClassName = (percent = 0) =>
    enviando && Number(percent) < 100
      ? "progresso-preenchido is-loading"
      : "progresso-preenchido";

  const getProgressWidth = (percent = 0) =>
    enviando && Number(percent) <= 0 ? "35%" : `${percent ?? 0}%`;

  // 🧠 Definição dinâmica dos tipos aceitos
  const baseAccept = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpeg", ".jpg"],
    "image/png": [".png"],
  };

  const docAccept = {
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.oasis.opendocument.text": [".odt"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.oasis.opendocument.spreadsheet": [".ods"],
  };

  const accept = permitirDocs ? { ...baseAccept, ...docAccept } : baseAccept;

  // DROPZONE
  const { getRootProps, getInputProps } = useDropzone({
    accept,
    multiple: true,
    onDrop: (acceptedFiles, rejectedFiles) => {
      const validFiles = acceptedFiles.filter(file => file.size <= MAX_FILE_SIZE);
      const arquivosGrandes = acceptedFiles.filter(file => file.size > MAX_FILE_SIZE);

      if (arquivosGrandes.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Arquivos muito grandes",
          html: `<span style="font-size: 0.9em">Os seguintes arquivos excedem o limite de 800 MB e não serão adicionados:</span><br><ul>${arquivosGrandes
            .map((f) => `<li style="font-size: 0.8em; text-align: left">${f.name} (${formatBytes(f.size)})</li>`)
            .join("")}</ul>`,
          width: 450,
          confirmButtonText: "Ok",
        });
      }

      // Adiciona arquivos aceitos
      setArquivos((curr) => {
        const total = curr.length + validFiles.length;
        if (total > MAX_FRONTEND_FILES) {
          Swal.fire({
            icon: "warning",
            title: "Limite de arquivos",
            text: `Você só pode enviar até ${MAX_FRONTEND_FILES} arquivos por vez.`,
          });
          // Adiciona apenas os que cabem          
          validFiles.splice(MAX_FRONTEND_FILES - curr.length);
        }

        const filesWithMetaData = validFiles.map((f) => {
          const fileKey = crypto.randomUUID();
          return (
            {
              file: f,
              clientId: f.name + "-" + fileKey,
              key: fileKey,
              percent: 0,
              bytesReceived: 0,
              totalBytes: f.size,
              status: "aguardando", //status inicial
            })
        });

        return [...curr, ...filesWithMetaData];
      });
      // Mostra erro se tiver arquivos rejeitados
      if (rejectedFiles.length > 0) {
        const tiposPermitidos = permitirDocs
          ? "PDF, imagens (JPG, PNG) e documentos do Word/Excel/LibreOffice (.doc, .docx, .odt, .xls, .xlsx, .ods)"
          : "apenas PDF e imagens (.jpg, .jpeg, .png)";
        Swal.fire({
          icon: "error",
          title: "Arquivo não permitido",
          text: `Os tipos permitidos são: ${tiposPermitidos}.`,
          confirmButtonText: "Ok",
        });
      }
    }
  });

  // Cursor aguardando
  useEffect(() => {
    document.body.style.cursor = enviando ? "wait" : "default";
    return () => { document.body.style.cursor = "default"; };
  }, [enviando]);

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.onchange = (e) => {
        const fileKey = crypto.randomUUID();
        const files = Array.from(e.target.files).map((f) => ({
          file: f,
          clientId: f.name + "-" + fileKey,
          key: fileKey,
          percent: 0,
          bytesReceived: 0,
          totalBytes: f.size,
          status: "aguardando",
        }));
        setArquivos(files);
        setUploadFinalizado(false);
        setProgresso(0);
      };
    }
    return () => {
      // Cleanup se sair do componente
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
        evtSourceRef.current = null;
      }
    };
  }, []);

  const iniciarSSE = (uploadId) => {
    const baseURL = api.defaults.baseURL;
    const url = `${baseURL}/upload-progress?uploadId=${uploadId}`;

    evtSourceRef.current = new EventSource(url);
    //console.log("SSE iniciado em:", url);

    // evtSourceRef.current.addEventListener("progress", (e) => {
    //   const { status, arquivos: arquivosDoSSE } = JSON.parse(e.data);
    //   // if (percent !== undefined) setProgresso(percent);

    //   console.log("Status SSE:", status);

    //   // Atualiza progresso de cada arquivo
    //   if (arquivosDoSSE && arquivosDoSSE.length) {
    //     setArquivos((curr) =>
    //       curr.map((item) => {
    //         const sseFile = arquivosDoSSE.find((f) => f.clientId === item.clientId);
    //         if (sseFile) {
    //           return {
    //             ...item,
    //             percent: sseFile.percent ?? item.percent,
    //             bytesReceived: sseFile.bytesReceived ?? item.bytesReceived,
    //             totalBytes: sseFile.totalBytes ?? item.totalBytes,
    //           };
    //         }
    //         return item;
    //       })
    //     );
    //   }

    //   if (status === "finalizado") {
    //     setUploadFinalizado(true);
    //     setEnviando(false);

    //     // ✅ se callback foi passado, chamar        
    //     // console.log("1 Passa para o componente pai: ", arquivosDoSSE);
    //     //if (typeof onUploadFinalizado === "function" && arquivosDoSSE?.length) {
    //     // console.log("2 Passa para o componente pai: ", arquivosDoSSE);
    //     //onUploadFinalizado(arquivosDoSSE); // ← envia para o componente pai
    //     //}
    //     setAtualizarArquivos(true);

    //     Swal.fire({
    //       icon: "success",
    //       title: "Upload concluído!",
    //       text: "Todos os arquivos foram enviados com sucesso.",
    //       width: 400,
    //     }).then(() => {
    //       setArquivos([]); // limpa a lista de arquivos
    //       setProgresso(0); // zera a barra de progresso          
    //       setUploadFinalizado(false);
    //       setExibirUploader(false);
    //     });
    //     evtSourceRef.current?.close();
    //     evtSourceRef.current = null;
    //   }

    //   if (status === "error") {
    //     setEnviando(false);
    //     Swal.fire({
    //       icon: "error",
    //       title: "Falha no upload",
    //       text: "Houve uma falha no envio dos arquivos.",
    //     });
    //     evtSourceRef.current?.close();
    //     evtSourceRef.current = null;
    //   }
    // });

    evtSourceRef.current.addEventListener("upload-progress", (e) => {
      // console.log("upload-progress", e.data);
      const payload = JSON.parse(e.data);

      // Atualiza progresso geral do lote (opcional)
      if (payload.percent !== undefined) setProgresso(payload.percent);

      // Atualiza progresso individual de cada arquivo
      if (payload.arquivos?.length) {
        setArquivos((curr) =>
          curr.map((item) => {
            const file = payload.arquivos?.find(f => f.key === item.key);
            if (file) {
              return {
                ...item,
                percent: file.percent ?? item.percent,
                bytesReceived: file.bytesReceived ?? item.bytesReceived,
                totalBytes: file.totalBytes ?? item.totalBytes,
                status: file.status ?? item.status,
                s3Key: file.s3Key ?? item.s3Key,
                error: file.error ?? item.error,
              };
            }
            return item;
          })
        );
      }
      // Finalização
      if (payload.status === "finalizado") {
        concluirUpload();
      }

      if (payload.status === "error") {
        setEnviando(false);
        Swal.fire({
          icon: "error",
          title: "Falha no upload",
          text: payload.message ?? "Houve uma falha no envio dos arquivos.",
        });

        evtSourceRef.current?.close();
        evtSourceRef.current = null;
      }

    });

    evtSourceRef.current.onerror = () => {
      // Caso a conexão SSE caia, fechar para evitar loop
      evtSourceRef.current?.close();
      evtSourceRef.current = null;
      if (enviando) {
        Swal.fire({
          icon: "error",
          title: "Erro na conexão com API",
          text: "A conexão para acompanhamento do progresso foi perdida.",
        });
        setEnviando(false);
      }
    };
  };

  const handleUpload = async () => {
    if (!arquivos.length) return;

    const uploadId = crypto.randomUUID();
    uploadConcluidoRef.current = false;

    const formData = new FormData();
    formData.append("uploadId", uploadId);
    formData.append("IDDiretorio", String(IDDiretorio));
    formData.append("tipoPermissao", String(tipoPermissao));
    formData.append("bucketName", bucketName);
    formData.append("IDCriador", IDOperador);

    // Adiciona os metadados com a chave única
    const arquivosInfo = arquivos.map((item) => ({
      key: item.key,
      originalName: item.file.name,
      size: item.totalBytes,
    }));
    formData.append("arquivosInfo", JSON.stringify(arquivosInfo));
    // Adiciona os arquivos
    arquivos.forEach((item) => formData.append("arquivos", item.file, item.file.name));

    setEnviando(true);
    setProgresso(0);
    setUploadFinalizado(false);

    // 🔹 Inicia SSE para acompanhar progresso
    iniciarSSE(uploadId);

    try {
      // arquivos.forEach((item, index) => {
      //   console.log(
      //     index,
      //     "name:", item.file.name,
      //     "size:", item.file.size,
      //     "type:", item.file.type,
      //     "key:", item.key,
      //   );
      // });


      // const res = await api.post("/upload-direto", formData, {
      //   headers: { "Content-Type": "multipart/form-data" },
      // });

      await api.post("/upload-direto", formData);
      window.dispatchEvent(new Event("assinatura:notifications-refresh"));
      concluirUpload();
      // console.log("Resposta do upload:", res.data);

      // Note que o SSE é responsável por avisar o fim do upload  
    } catch (err) {
      console.error("Erro no upload:", err);
      setEnviando(false);
      // Axios error
      if (err.response && err.response.data && err.response.data.error) {
        Swal.fire({
          icon: "error",
          title: "Falha no upload",
          text: err.response.data.error,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Falha no upload",
          text: "Houve uma falha ao tentar enviar os arquivos.",
        });
      }
      evtSourceRef.current?.close();
      evtSourceRef.current = null;
    }
  };

  const concluirUpload = () => {
    if (uploadConcluidoRef.current) return;

    uploadConcluidoRef.current = true;
    setUploadFinalizado(true);
    setEnviando(false);
    setProgresso(100);
    setAtualizarArquivos(true);
    window.dispatchEvent(new Event("assinatura:notifications-refresh"));

    evtSourceRef.current?.close();
    evtSourceRef.current = null;

    Swal.fire({
      icon: "success",
      title: "Upload concluído!",
      text: "Todos os arquivos foram enviados com sucesso.",
      width: 400,
    }).then(() => {
      setArquivos([]);
      setProgresso(0);
      setUploadFinalizado(false);
      setExibirUploader(false);
    });
  };

  function formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  return (
    <div className="upload-container">
      {!uploadFinalizado && !enviando && (
        <>
          <div className="drag-drop-area" {...getRootProps()}>
            <input {...getInputProps()} />
            <RiDragDropLine style={{ fontSize: 58, margin: 5 }} />
            <p>Arraste e solte arquivos aqui ou clique para selecionar</p>
          </div>
          <IoCloseCircle className="botaoFecharUploader"
            onClick={() => { if (!enviando) setExibirUploader(false); }}
            style={{ cursor: enviando ? "not-allowed" : "pointer", opacity: enviando ? 0.5 : 1 }} />
        </>
      )}

      {arquivos.length > 0 && !uploadFinalizado && (
        <>
          <div style={{ placeSelf: 'auto', textAlign: 'left' }}>
            <ul style={{ listStyleType: 'none', padding: 0, columnCount: 1, columnGap: 40 }}>
              {arquivos.map((item, i) => (
                // <span key={i} title={file.name}>
                <div className="row" key={item.key}>
                  <li title={item.file.name} className="listaTituloArquivo" style={{ marginBottom: '10px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <LiaCloudUploadAltSolid style={{ color: 'blue' }} />
                      <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.9rem' }}>
                        {item.file.name.length > 100 ? item.file.name.slice(0, 100) + '...' : item.file.name}
                      </span>
                      <span style={{ minWidth: '120px', fontSize: '0.8rem', textAlign: 'right' }}>
                        {item.percent ?? 0}% &nbsp; {item.status ?? "aguardando"} <br />
                        {formatBytes(item.bytesReceived ?? 0)} / {formatBytes(item.totalBytes ?? 0)}
                      </span>
                    </div>
                    <div className="barra-progresso" style={{ marginTop: 4 }}>
                      <div
                        className={getProgressClassName(item.percent)}
                        style={{ width: getProgressWidth(item.percent) }}
                      />
                    </div>
                  </li>
                </div>
                // </span>
              ))}
            </ul>
          </div>
          {!enviando && (
            <button className="upload-action-button" onClick={handleUpload}>
              Enviar arquivos
            </button>
          )}
        </>
      )
      }

      {enviando && (
        <p className="upload-status-geral">
          Enviando arquivos... {progresso > 0 ? `${progresso}%` : ""}
        </p>
      )}

      {uploadFinalizado && <ResultadoUpload arquivos={arquivos} />}
    </div >
  );
}
